import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Camera, X, FileText, Paperclip } from "lucide-react";

export function QuickJobDialog({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ recipient_name: "", recipient_address: "", client_name: "", documents: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.recipient_name.trim()) return toast.error("Who is being served?");
    setBusy(true);
    try {
      const { data: job } = await api.post("/jobs/quick", f);
      let ok = 0;
      for (let i = 0; i < files.length; i++) {
        setProgress(`Uploading ${i + 1} of ${files.length}…`);
        const fd = new FormData(); fd.append("file", files[i]);
        try { await api.post(`/jobs/${job.id}/documents`, fd, { timeout: 120000 }); ok++; }
        catch (ex) { toast.error(`${files[i].name}: ${ex?.response?.data?.detail || "upload failed"}`); }
      }
      toast.success(`${job.job_number} created${files.length ? ` · ${ok}/${files.length} attachment(s)` : ""} — flagged for details`);
      onCreated?.(job);
      onClose();
      navigate(`/jobs/${job.id}`);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not create job"); }
    finally { setBusy(false); setProgress(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-job-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-4 w-4" />Picked up a job in person</DialogTitle>
            <DialogDescription>Snap or attach the documents and jot the basics. This creates an open job assigned to you so nothing slips — the office fills in the rest later. You can log an attempt right away.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="sm-label">Who is being served <span className="text-rose-600">*</span></label><Input value={f.recipient_name} onChange={set("recipient_name")} placeholder="Name on the documents" autoFocus data-testid="quick-recipient" /></div>
            <div><label className="sm-label">Service address <span className="text-slate-400 font-normal">(if known)</span></label><Input value={f.recipient_address} onChange={set("recipient_address")} placeholder="Street, City, State ZIP" data-testid="quick-address" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="sm-label">Client / who handed it to you</label><Input value={f.client_name} onChange={set("client_name")} placeholder="Firm or person" data-testid="quick-client" /></div>
              <div><label className="sm-label">Documents</label><Input value={f.documents} onChange={set("documents")} placeholder="e.g. Summons & Complaint" data-testid="quick-documents" /></div>
            </div>
            <div><label className="sm-label">Notes</label><Textarea rows={2} value={f.notes} onChange={set("notes")} placeholder="Anything the office should know" data-testid="quick-notes" /></div>
            <div>
              <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="quick-files-input" />
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="quick-camera-input" />
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => camRef.current?.click()} className="rounded-lg bg-[#0b1f3a] hover:bg-[#1f5fa8] text-white py-4 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="quick-camera-button"><Camera className="h-4 w-4" />Take photo of pages</button>
                <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg border-2 border-dashed border-slate-300 hover:border-[#2d7bd0] bg-slate-50 py-4 text-sm text-slate-600 inline-flex items-center justify-center gap-2" data-testid="quick-files-button"><Paperclip className="h-4 w-4" />Attach file / PDF</button>
              </div>
              {files.length > 0 && <div className="mt-2 text-[11px] text-slate-500">Tap "Take photo" again for each page.</div>}
              {files.length > 0 && <ul className="mt-2 space-y-1">{files.map((fl, i) => <li key={i} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1"><FileText className="h-3 w-3 text-slate-400" /><span className="truncate flex-1">{fl.name}</span><span className="text-slate-400">{(fl.size / 1e6).toFixed(1)} MB</span><button type="button" onClick={() => setFiles(files.filter((_, k) => k !== i))} aria-label="Remove"><X className="h-3 w-3 text-slate-400" /></button></li>)}</ul>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {progress && <span className="text-xs text-slate-500 self-center mr-auto">{progress}</span>}
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" className="sm-green" disabled={busy} data-testid="quick-job-submit">{busy ? "Saving…" : "Create job"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
